import type { KalkulationsZeile, PositionsKalkulation } from './schema';

/**
 * Reine Rechenhelfer für die Positions-Kalkulation (Tiefen-/Blitzkalkulation).
 *
 * Modell: `mengenBezug` legt fest, worauf sich die Kalk-Summe (gesamtNetto) bezieht:
 * - 'einheit': die Kalkulation gilt JE EINHEIT ⇒ EP = gesamtNetto (keine Division).
 *              Das ist der Default für NEU angelegte Kalkulationen.
 * - 'gesamt':  die Kalkulation gilt für die GESAMTE Positionsmenge ⇒ EP = gesamtNetto / Menge.
 *              Das ist der Default, wenn `mengenBezug` fehlt (Bestandsdaten) — so bleiben
 *              Einheitspreise bestehender Angebote beim Laden stabil.
 *
 * - lohn:      menge = Zeit (zeitEinheit 'h' oder 'min', Fallback 'h'), einzelpreis = Stundensatz
 *              (Fallback: defaults.stundensatz)
 * - material:  menge = Stückzahl (Fallback 1), einzelpreis = EK,
 *              aufschlagProzent (Fallback: defaults.materialAufschlagProzent)
 * - sonstiges: menge = Stückzahl (Fallback 1), einzelpreis = Betrag (darf negativ sein)
 */

export interface KalkulationsDefaults {
  stundensatz: number;
  materialAufschlagProzent: number;
}

export const KALKULATION_FALLBACK_DEFAULTS: KalkulationsDefaults = {
  stundensatz: 65,
  materialAufschlagProzent: 15,
};

export interface KalkulationsSummen {
  lohnStunden: number;
  lohnSumme: number;
  materialEkSumme: number;
  materialAufschlagSumme: number;
  materialSumme: number; // EK + Aufschlag
  sonstigesSumme: number;
  gesamtNetto: number;
}

function toNumber(value: unknown, fallback = 0): number {
  const n = typeof value === 'string' ? Number(value.replace(',', '.')) : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function roundMoney(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** Effektiver Stundensatz für eine Lohnzeile. */
export function effektiverStundensatz(
  zeile: KalkulationsZeile,
  kalkulation: Pick<PositionsKalkulation, 'stundensatz'> | null | undefined,
  defaults: KalkulationsDefaults,
): number {
  const zeilenSatz = toNumber(zeile.einzelpreis, Number.NaN);
  if (Number.isFinite(zeilenSatz) && zeilenSatz > 0) return zeilenSatz;
  const positionsSatz = toNumber(kalkulation?.stundensatz, Number.NaN);
  if (Number.isFinite(positionsSatz) && positionsSatz > 0) return positionsSatz;
  return defaults.stundensatz;
}

/** Effektiver Material-Aufschlag (%) für eine Materialzeile. */
export function effektiverMaterialAufschlag(
  zeile: KalkulationsZeile,
  kalkulation: Pick<PositionsKalkulation, 'materialAufschlagProzent'> | null | undefined,
  defaults: KalkulationsDefaults,
): number {
  const zeilenAufschlag = toNumber(zeile.aufschlagProzent, Number.NaN);
  if (Number.isFinite(zeilenAufschlag) && zeilenAufschlag >= 0) return zeilenAufschlag;
  const positionsAufschlag = toNumber(kalkulation?.materialAufschlagProzent, Number.NaN);
  if (Number.isFinite(positionsAufschlag) && positionsAufschlag >= 0) return positionsAufschlag;
  return defaults.materialAufschlagProzent;
}

/** Lohnzeilen-Menge in STUNDEN (rechnet 'min' anhand zeitEinheit um). */
function lohnStundenAusZeile(zeile: KalkulationsZeile): number {
  const wert = Math.max(0, toNumber(zeile.menge));
  return zeile.zeitEinheit === 'min' ? wert / 60 : wert;
}

/** Netto-Summe einer einzelnen Kalkulationszeile (inkl. Material-Aufschlag). */
export function zeilenSumme(
  zeile: KalkulationsZeile,
  kalkulation: PositionsKalkulation | null | undefined,
  defaults: KalkulationsDefaults = KALKULATION_FALLBACK_DEFAULTS,
): number {
  if (zeile.art === 'lohn') {
    const stunden = lohnStundenAusZeile(zeile);
    return roundMoney(stunden * effektiverStundensatz(zeile, kalkulation, defaults));
  }
  const stueck = zeile.menge === undefined || zeile.menge === null ? 1 : Math.max(0, toNumber(zeile.menge, 1));
  if (zeile.art === 'material') {
    const einzel = Math.max(0, toNumber(zeile.einzelpreis));
    const basis = stueck * einzel;
    const aufschlag = effektiverMaterialAufschlag(zeile, kalkulation, defaults);
    return roundMoney(basis * (1 + aufschlag / 100));
  }
  // sonstiges: Betrag darf negativ sein (z.B. Rabatt-Zeile aus dem Gesamtaufschlag)
  const einzel = toNumber(zeile.einzelpreis);
  return roundMoney(stueck * einzel);
}

/** Alle Summen einer Positions-Kalkulation. */
export function berechneKalkulation(
  kalkulation: PositionsKalkulation | null | undefined,
  defaults: KalkulationsDefaults = KALKULATION_FALLBACK_DEFAULTS,
): KalkulationsSummen {
  const summen: KalkulationsSummen = {
    lohnStunden: 0,
    lohnSumme: 0,
    materialEkSumme: 0,
    materialAufschlagSumme: 0,
    materialSumme: 0,
    sonstigesSumme: 0,
    gesamtNetto: 0,
  };
  if (!kalkulation?.zeilen?.length) return summen;

  for (const zeile of kalkulation.zeilen) {
    const summe = zeilenSumme(zeile, kalkulation, defaults);
    if (zeile.art === 'lohn') {
      summen.lohnStunden += lohnStundenAusZeile(zeile);
      summen.lohnSumme = roundMoney(summen.lohnSumme + summe);
    } else if (zeile.art === 'material') {
      const stueck = zeile.menge === undefined || zeile.menge === null ? 1 : Math.max(0, toNumber(zeile.menge, 1));
      const ek = roundMoney(stueck * Math.max(0, toNumber(zeile.einzelpreis)));
      summen.materialEkSumme = roundMoney(summen.materialEkSumme + ek);
      summen.materialSumme = roundMoney(summen.materialSumme + summe);
    } else {
      summen.sonstigesSumme = roundMoney(summen.sonstigesSumme + summe);
    }
  }
  summen.materialAufschlagSumme = roundMoney(summen.materialSumme - summen.materialEkSumme);
  summen.gesamtNetto = roundMoney(summen.lohnSumme + summen.materialSumme + summen.sonstigesSumme);
  summen.lohnStunden = Math.round(summen.lohnStunden * 100) / 100;
  return summen;
}

/**
 * Einheitspreis aus Kalkulations-Gesamtsumme und Positionsmenge (2 Nachkommastellen).
 *
 * mengenBezug === 'einheit'            ⇒ EP = gesamtNetto (Kalkulation gilt je Einheit)
 * mengenBezug === 'gesamt' | fehlt      ⇒ EP = gesamtNetto / Positionsmenge (Bestandsverhalten)
 */
export function einheitspreisAusKalkulation(
  kalkulation: PositionsKalkulation | null | undefined,
  positionsMenge: number,
  defaults: KalkulationsDefaults = KALKULATION_FALLBACK_DEFAULTS,
): number {
  const { gesamtNetto } = berechneKalkulation(kalkulation, defaults);
  if (kalkulation?.mengenBezug === 'einheit') {
    return gesamtNetto;
  }
  const menge = toNumber(positionsMenge);
  if (!Number.isFinite(menge) || menge <= 0) return gesamtNetto;
  return roundMoney(gesamtNetto / menge);
}

/** Gesamtbetrag (Menge × EP) für die Fußzeile der Tiefenkalkulation — immer als Kontrollwert anzeigbar. */
export function gesamtAusEinheitspreis(einheitspreis: number, positionsMenge: number): number {
  const menge = toNumber(positionsMenge);
  const effektiveMenge = Number.isFinite(menge) && menge > 0 ? menge : 1;
  return roundMoney(einheitspreis * effektiveMenge);
}

/**
 * True, wenn die Kalkulation mindestens eine wertbehaftete Zeile enthält.
 *
 * P1-B (Fund): die frühere Prüfung `menge>0 || einzelpreis>0` erkannte eine reine
 * negative `sonstiges`-Rabattzeile (z.B. der globale Gesamtaufschlag/-rabatt aus
 * {@link wendeGesamtaufschlagAn}, menge=0 Default + einzelpreis<0) NICHT als befüllt.
 * Ein nachfolgender Preis-Import (siehe {@link mengenBezugFuerPreisImport}) hätte
 * dadurch fälschlich `hatteVorherKalkulation=false` gesehen und die Kalkulation auf
 * `mengenBezug: 'einheit'` umgestempelt — der bestehende GESAMT-Rabatt würde dadurch
 * rückwirkend zu einem Pro-Einheit-Rabatt ×Menge (stiller Fehlpreis). Jede Zeile mit
 * einem tatsächlichen Wert zählt daher — auch negativ, auch nur menge ODER nur
 * einzelpreis gesetzt. Nur eine echte Nullzeile (menge=0 UND einzelpreis=0/fehlt)
 * bleibt "leer".
 */
export function hatKalkulation(kalkulation: PositionsKalkulation | null | undefined): boolean {
  return Boolean(
    kalkulation?.zeilen?.some(
      (z) => toNumber(z.menge) !== 0 || toNumber(z.einzelpreis) !== 0,
    ),
  );
}

/**
 * P1-1 (Fix, Codex-Fund): `berechneKalkulation(...).gesamtNetto` einer 'einheit'-Position ist ein
 * PRO-EINHEIT-Betrag (siehe Kopf-Kommentar dieser Datei). Aggregat-Pfade, die über ALLE Positionen
 * summieren (Blitzkalkulations-Kopfzeile in blitzkalkulation-view.tsx, Gesamtblock in
 * server/urkalkulation-pdf-generator.ts::berechneGesamtSummen), addierten diesen Betrag bisher
 * EINMAL pro Position, statt ihn mit der Positionsmenge zu multiplizieren — Kostenübersicht/
 * Urkalkulation unterschätzten die tatsächlichen Kosten um genau den Mengenfaktor (z.B. 522 €
 * Kalk-Summe bei Menge 10 flossen als 522 € statt 5.220 € in die Gesamtsumme ein).
 *
 * Diese Funktion liefert den korrekten AGGREGAT-BEITRAG einer einzelnen Position: bei
 * mengenBezug 'einheit' werden alle Komponenten-Summen (Lohnstunden, Lohn, Material, Sonstiges,
 * Netto) mit der Positionsmenge multipliziert; bei 'gesamt' (bzw. fehlendem Feld, Bestandsdaten)
 * bleibt die Kalk-Summe unverändert (sie gilt dort bereits für die GESAMTE Position). Beide
 * Aggregat-Pfade MÜSSEN ausschließlich über diese Funktion summieren, nie direkt über
 * berechneKalkulation(), damit der Faktor nicht erneut vergessen wird.
 */
export function positionsGesamtbeitrag(
  kalkulation: PositionsKalkulation | null | undefined,
  positionsMenge: number,
  defaults: KalkulationsDefaults = KALKULATION_FALLBACK_DEFAULTS,
): KalkulationsSummen {
  const summen = berechneKalkulation(kalkulation, defaults);
  if (kalkulation?.mengenBezug !== 'einheit') return summen;
  const menge = toNumber(positionsMenge);
  const faktor = Number.isFinite(menge) && menge > 0 ? menge : 1;
  return {
    // P2-3 (Fix, Codex-Review gpt-5.6): summen.lohnStunden ist bereits von berechneKalkulation()
    // auf 2 Dezimalen GERUNDET (s.o.) — dieses gerundete Ergebnis mit `faktor` zu skalieren
    // verstärkt den Rundungsfehler (z.B. 1 min/Einheit ⇒ 0,02 h gerundet; × Menge 100 ⇒ "2,00 h"
    // statt korrekt 1,67 h, obwohl die Lohnkosten selbst — lohnSumme — bereits die tatsächlichen
    // 100 min repräsentieren). Fix: für lohnStunden die ROHEN (ungerundeten) Zeilendauern separat
    // aufsummieren (rohLohnStundenSumme), MIT der Positionsmenge skalieren und ERST DANACH runden
    // — nur dieser eine Aggregat-/PDF-Feld wird anders berechnet, der Per-Position-EP
    // (berechneKalkulation/einheitspreisAusKalkulation, die weiterhin die gerundete Summe nutzen)
    // bleibt unverändert.
    lohnStunden: roundMoney(rohLohnStundenSumme(kalkulation) * faktor),
    lohnSumme: roundMoney(summen.lohnSumme * faktor),
    materialEkSumme: roundMoney(summen.materialEkSumme * faktor),
    materialAufschlagSumme: roundMoney(summen.materialAufschlagSumme * faktor),
    materialSumme: roundMoney(summen.materialSumme * faktor),
    sonstigesSumme: roundMoney(summen.sonstigesSumme * faktor),
    gesamtNetto: roundMoney(summen.gesamtNetto * faktor),
  };
}

/**
 * Rohsumme (UNGERUNDET) der Lohnstunden aller Zeilen — Grundlage für den mengenskalierten
 * Aggregat-Beitrag oben (siehe P2-3-Kommentar in {@link positionsGesamtbeitrag}). Bewusst NICHT
 * über berechneKalkulation() bezogen, weil dessen summen.lohnStunden bereits auf 2 Dezimalen
 * gerundet ist — genau das Runden VOR der Mengen-Skalierung war der Fund.
 */
function rohLohnStundenSumme(kalkulation: PositionsKalkulation | null | undefined): number {
  if (!kalkulation?.zeilen?.length) return 0;
  let summe = 0;
  for (const zeile of kalkulation.zeilen) {
    if (zeile.art === 'lohn') summe += lohnStundenAusZeile(zeile);
  }
  return summe;
}

/**
 * P2-5 (Fix, REGRESSION aus P1-B): `hatKalkulation` erkennt (absichtlich, für die
 * Import-Konflikt-Erkennung) auch eine reine NEGATIVE Zeile (z.B. ein Rabatt ohne zugehörige
 * Kostenzeile) als "befüllt". Für die Frage "darf aus dieser Kalkulation ein Einzelpreis
 * abgeleitet und GESPEICHERT werden?" ist das falsch: eine Kalkulation, deren Netto-Summe
 * (gesamtNetto) NICHT POSITIV ist (0 oder negativ), darf NICHT als gültige EP-Quelle gelten —
 * sonst leitet einheitspreisAusKalkulation() einen negativen (oder unbeabsichtigt exakt
 * 0,00 €) Einzelpreis ab und ein gültiger Bestandspreis wird überschrieben/negativ.
 *
 * Verwendet vom Modal-Save-Gate (client/src/components/kalkulation/position-kalkulation-modal.tsx)
 * UND vom EP-Ableitungspfad in client/src/lib/store.ts::updatePositionKalkulation — beide MÜSSEN
 * dieses Prädikat statt hatKalkulation() für die EP-Gültigkeit prüfen. hatKalkulation() bleibt für
 * die Import-Konflikt-Erkennung (mengenBezugFuerPreisImport) unverändert bestehen — dort ist eine
 * negative Zeile weiterhin korrekt als "befüllt" zu werten (Bestandsschutz-Zweck, siehe dort).
 */
export function istKalkulationGueltigFuerEpAbleitung(
  kalkulation: PositionsKalkulation | null | undefined,
  defaults: KalkulationsDefaults = KALKULATION_FALLBACK_DEFAULTS,
): boolean {
  if (!kalkulation?.zeilen?.length) return false;
  const { gesamtNetto } = berechneKalkulation(kalkulation, defaults);
  return gesamtNetto > 0;
}

/**
 * P2-5 (Fund): client/src/lib/store.ts::updatePositionKalkulation überschrieb
 * `position.kalkulation` bisher IMMER (auch wenn istKalkulationGueltigFuerEpAbleitung()
 * die EP-Ableitung ablehnt, z.B. ein globaler −100%-Rabatt über
 * {@link wendeGesamtaufschlagAn}, der eine befüllte Kalkulation mit gesamtNetto=0
 * erzeugt) — `position.preis` blieb in diesem Fall unverändert stehen (der ALTE Wert).
 * Ergebnis: die gespeicherte Kalkulation (zeigt z.B. "Rabatt −100 %, Endbetrag 0,00 €")
 * und der gespeicherte Angebotspreis (der alte, unveränderte Betrag) widersprechen sich
 * sichtbar — dieselbe Fehlerklasse wie ein stiller Fehlpreis, nur umgekehrt (die
 * KORREKTE Preisänderung wird still verworfen, während die Kalkulation einen bereits
 * abgeschlossenen Zustand vorgaukelt).
 *
 * Fix: dieselbe Ablehnung, die position-kalkulation-modal.tsx bereits über ein
 * deaktiviertes Save-Button (`kalkulationUngueltigFuerEp`) erzwingt, GREIFT VOR DEM
 * PERSISTIEREN — für ALLE Aufrufer von updatePositionKalkulation (Blitzkalkulation-
 * Zelleneingabe, Gesamtaufschlag, Katalog-/X84-Preisimporte). Eine BEFÜLLTE
 * (hatKalkulation) aber für die EP-Ableitung UNGÜLTIGE Kalkulation (Netto ≤ 0) wird GAR
 * NICHT persistiert — weder Kalkulation noch Preis ändern sich, der vorherige
 * konsistente Zustand bleibt erhalten. Eine leere/entfernte Kalkulation
 * (`normalizedKalkulation === null`) sowie eine Kalkulation mit reinen Platzhalter-
 * zeilen (menge=0 UND einzelpreis=0, `hatKalkulation` also false) bleiben davon
 * UNBERÜHRT — das Entfernen einer Kalkulation bzw. das Anlegen einer noch leeren
 * Zeile beim interaktiven Editieren ist weiterhin immer erlaubt (kein Sperren der
 * normalen Blitzkalkulations-Eingabe).
 */
export function darfKalkulationPersistiertWerden(
  normalizedKalkulation: PositionsKalkulation | null,
  defaults: KalkulationsDefaults = KALKULATION_FALLBACK_DEFAULTS,
): boolean {
  if (normalizedKalkulation === null) return true;
  if (!hatKalkulation(normalizedKalkulation)) return true;
  return istKalkulationGueltigFuerEpAbleitung(normalizedKalkulation, defaults);
}

/**
 * Stempelt eine frisch aus einem Preis-Import (Katalog-Vorschlag / X84-Preisrückimport,
 * Modus "material-ek") gebaute Kalkulation auf `mengenBezug: 'einheit'` um.
 *
 * Hintergrund (EP-Bug): Der importierte Preis (Katalog-EK bzw. X84-EP) ist inhärent PRO
 * EINHEIT. Für eine ECHT brandneue Kalkulation (kein Objekt vorhanden) setzt `setzeBlitzZeile`
 * seit der Umstellung auf den 'einheit'-Default bereits das Richtige — diese Funktion ist für
 * DIESEN Fall also ein reiner (harmloser) No-op-Re-Stempel. Sie bleibt aber NICHT überflüssig:
 * `setzeBlitzZeile` übernimmt bei einer bereits VORHANDENEN Kalkulation deren `mengenBezug`
 * unverändert per Spread (auch wenn das Feld dort fehlt oder `'gesamt'` ist) — z.B. eine
 * bestehende Kalkulation, die nur Nullzeilen enthält (`hatKalkulation` = false, aber das Objekt
 * existiert bereits mit `mengenBezug` fehlend/`'gesamt'`). Für genau diesen Fall (VORHER
 * unbefüllt, aber Kalkulation-Objekt bereits vorhanden) MUSS hier weiterhin explizit auf
 * `'einheit'` umgestempelt werden, sonst würde `einheitspreisAusKalkulation` den bereits pro
 * Einheit vorliegenden importierten Preis ein zweites Mal durch die Positionsmenge teilen.
 *
 * NUR anwenden, wenn die Position VORHER keine befüllte Kalkulation hatte
 * (`hatteVorherKalkulation` = false, geprüft über {@link hatKalkulation} auf dem Stand VOR
 * `setzeBlitzZeile`). Bei einer bereits bestehenden BEFÜLLTEN Kalkulation (z.B. `'gesamt'` mit
 * anderen, manuell erfassten Zeilen) bleibt deren `mengenBezug` unangetastet — ein Umstempeln
 * würde die anderen Zeilen rückwirkend umdeuten (Bestandsschutz, kein stiller Auto-Fix).
 */
export function mengenBezugFuerPreisImport(
  kalkulation: PositionsKalkulation,
  hatteVorherKalkulation: boolean,
): PositionsKalkulation {
  if (hatteVorherKalkulation) return kalkulation;
  return { ...kalkulation, mengenBezug: 'einheit' };
}

/**
 * FUND 1(c): True, wenn ein Preis-Import (Katalog-Vorschlag / X84-Preisrückimport,
 * Modus "material-ek") auf DIESER Kalkulation NICHT sicher automatisch anwendbar ist
 * und deshalb in der UI blockiert werden muss.
 *
 * Hintergrund: `setzeBlitzZeile` trägt die Material-Importzeile immer mit menge=1
 * (bzw. der bereits vorhandenen Blitz-Menge) ein — der importierte Preis ist
 * inhärent EIN Wert PRO EINHEIT. Bei `mengenBezug: 'einheit'` ist das korrekt
 * (EP = gesamtNetto, keine Division). Bei einer bereits BEFÜLLTEN 'gesamt'-Kalkulation
 * (inkl. Bestandsdaten ohne `mengenBezug`-Feld, siehe Kopf-Kommentar dieser Datei)
 * würde die neue Zeile nur EK×1 zum gesamtNetto beitragen, das anschließend durch die
 * Positionsmenge geteilt wird ⇒ der importierte EK wäre um den Faktor Positionsmenge
 * zu NIEDRIG — still, ohne jede Fehlermeldung.
 *
 * Geprüfte, aber VERWORFENE Alternative: der Material-Importzeile `menge = Positionsmenge`
 * geben, damit ihr Beitrag EK×Menge ist und nach der Division exakt EK pro Einheit ergibt.
 * Das behebt den Faktor-Menge-Fehler nur EINMALIG zum Importzeitpunkt — die Blitz-Zeilen-
 * Menge trackt eine SPÄTERE Änderung der Positionsmenge nicht automatisch nach (anders als
 * bei 'einheit', wo gar keine Division stattfindet). Nach einer Mengenänderung würde der
 * eingefrorene Menge-Wert einen NEUEN, ebenso stillen Fehler erzeugen (in Gegenrichtung:
 * über- oder unterpreist, je nach Richtung der Änderung). Deshalb: BLOCKIEREN statt
 * automatisch "korrigieren" — kein stiller Fehlpreis ist wichtiger als Komfort.
 *
 * Bei `mengenBezug: 'einheit'` (inkl. einer brandneuen Kalkulation, die über
 * {@link mengenBezugFuerPreisImport} darauf gestempelt wird) bleibt der Import
 * uneingeschränkt möglich — dort ist die bestehende Rechnung bereits korrekt.
 *
 * Review-Auflage B (GPT-5.6, 2026-08): der Import ist NUR bei EXPLIZITEM
 * `mengenBezug === 'einheit'` erlaubt — ein FEHLENDES Feld MUSS blockiert bleiben, denn
 * Bestandsdaten ohne Feld rechnen effektiv 'gesamt' (siehe Kopf-Kommentar dieser Datei).
 * Deshalb der strikte Vergleich `!== 'einheit'` auf dem ROHEN Feld: `undefined` fällt in
 * den Block. Hier darf NIE ein Default eingesetzt werden — ein `?? 'einheit'` an dieser
 * Stelle würde jede Bestandskalkulation ohne Feld für den Import freigeben und den
 * importierten EK still durch die Positionsmenge teilen (exakt der 0,16-€-Vorfall).
 * Regressions-Anker: tests/kalkulation-mengenbezug-schutz.test.ts.
 */
export function materialEkImportBlockiertDurchBestandskalkulation(
  kalkulation: PositionsKalkulation | null | undefined,
): boolean {
  return hatKalkulation(kalkulation) && kalkulation?.mengenBezug !== 'einheit';
}

/**
 * FUND 1(d): True, wenn ein tatsächlich positiver effektiver Einzelpreis nach der
 * 2-Dezimal-Rundung (siehe {@link roundMoney}, wie sie beim Setzen von `Position.preis`
 * bzw. innerhalb der Kalkulationszeilen angewendet wird) still auf 0,00 € fallen würde
 * (z.B. Katalog "1000 ST" zu 3,50 € ⇒ 0,0035 €/St). Ein ECHTER Gratis-Preis
 * (effektiverPreis === 0) ist davon NICHT betroffen — 0,00 € ist dort das korrekte,
 * gewollte Ergebnis, kein Rundungsartefakt.
 *
 * Wird in den Preis-Import-Dialogen genutzt, um "Übernehmen" zu deaktivieren, statt
 * eine still falsche 0,00-€-Zeile mit "übernommen"-Toast zu erzeugen.
 */
export function rundetStillAufNull(effektiverPreis: number): boolean {
  return Number.isFinite(effektiverPreis) && effektiverPreis > 0 && roundMoney(effektiverPreis) === 0;
}

/**
 * P1-E: True, wenn ein Preis-Import im Modus "direkt-ep" (setzt NUR `Position.preis`,
 * lässt eine vorhandene Kalkulation unangetastet) für DIESE Position nicht sicher ist,
 * weil bereits eine befüllte Kalkulation existiert.
 *
 * Fund: `updatePosition({ preis })` ändert ausschließlich den VK-Preis — die Kalkulation
 * bleibt unverändert stehen. Der nächste Kalkulations-Speichervorgang (Blitz- ODER
 * Tiefenkalkulation) leitet den EP aber wieder aus `einheitspreisAusKalkulation()` ab und
 * überschreibt damit den gerade importierten Direktwert kommentarlos — der eigene
 * Block-Hinweis für "material-ek" empfahl bislang genau diesen Modus als Workaround, also
 * einen kaputten Weg. Deshalb: "direkt-ep" ebenfalls blockieren, sobald eine Kalkulation
 * bereits befüllt ist (unabhängig von `mengenBezug` — auch bei 'einheit' überschreibt der
 * nächste Kalk-Speichervorgang den Direktwert). Kein stiller Widerspruch zwischen
 * angezeigtem und tatsächlich gespeichertem Preis.
 */
export function direktEpImportBlockiertDurchBestandskalkulation(
  kalkulation: PositionsKalkulation | null | undefined,
): boolean {
  return hatKalkulation(kalkulation);
}

/**
 * P2-F: True, wenn ein tatsächlich importierter effektiver Einzelpreis <= 0 ist. Ein
 * solcher Wert wird von {@link setzeBlitzZeile} als "leer" behandelt (die Blitzzeile wird
 * ENTFERNT statt auf 0 gesetzt, weil eine Blitzkalkulation mit Betrag 0 nicht von "keine
 * Eingabe" unterscheidbar ist) — ein material-ek-Import mit EK<=0 hätte dadurch STILL
 * keine Wirkung (die alte Kalkulation/der alte EP bleiben unverändert bestehen), während
 * die UI trotzdem "übernommen" meldet. Anders als ein bewusst manuell eingetragener
 * Gratis-Preis (siehe {@link rundetStillAufNull}, das einen ECHTEN 0-€-Preis ausdrücklich
 * NICHT blockiert) ist ein EK von 0/negativ aus einem Katalog-Import kein plausibler,
 * unmissverständlicher Wert — deshalb konsistent als nicht-übernehmbar blockieren statt
 * einen widersprüchlichen Erfolg zu zeigen.
 */
export function nichtUebernehmbarWegenNullOderNegativemPreis(effektiverPreis: number): boolean {
  return Number.isFinite(effektiverPreis) && effektiverPreis <= 0;
}

/**
 * P1-1 (Fund, HÖCHSTE PRIORITÄT): der Katalog-/X84-Preis-Import stempelt eine leere Position
 * korrekt auf `mengenBezug: 'einheit'` (siehe {@link mengenBezugFuerPreisImport}) — das ist
 * für die importierte Material-EK-Zeile richtig (der EK ist inhärent PRO EINHEIT). Trägt der
 * Nutzer DANACH über die Blitzkalkulation (client/src/components/kalkulation/blitzkalkulation-view.tsx)
 * Lohn-Stunden oder Sonstiges nach, interpretiert das System diese Werte GENAUSO als "je
 * Einheit" — obwohl die Blitz-UI dort implizit "für die GESAMTE Position" suggeriert (die
 * Spaltenbeschriftungen sind identisch zur 'gesamt'-Blitzkalkulation, es gibt keinen
 * sichtbaren Hinweis auf den 'einheit'-Modus). Ergebnis: ein eingetragener Betrag wird beim
 * Gesamt-Betrag (Menge × EP) MULTIPLIKATIV mit der Positionsmenge versehen, obwohl er als
 * Gesamtwert gemeint war (z.B. 370 € gemeint, 7.230 € berechnet bei Menge 20).
 *
 * Die Gefahr entsteht NUR beim Mischen: eine reine Material-`'einheit'`-Kalkulation ohne
 * zusätzliche Lohn-/Sonstiges-Zeile ist unproblematisch (genau dafür ist der Stempel da). Erst
 * wenn eine Lohn- ODER Sonstiges-Zeile zu einer bereits befüllten Material-Zeile hinzukommt,
 * wird der resultierende Betrag missverständlich. Reine Entscheidungsfunktion (kein
 * UI-Zugriff), damit sie ohne .tsx unter node:test geprüft werden kann — die Sichtbarkeit
 * (Badge) selbst lebt in blitzkalkulation-view.tsx.
 */
export function warntMischungEinheitMitPositionszeile(
  kalkulation: PositionsKalkulation | null | undefined,
  neueZeilenArt: 'lohn' | 'material' | 'sonstiges',
): boolean {
  if (kalkulation?.mengenBezug !== 'einheit') return false;
  if (neueZeilenArt !== 'lohn' && neueZeilenArt !== 'sonstiges') return false;
  const hatBefuellteMaterialzeile = hatKalkulation({
    zeilen: kalkulation.zeilen.filter((z) => z.art === 'material'),
  });
  return hatBefuellteMaterialzeile;
}

/**
 * P1-1: Sichtbarkeits-Bedingung für den "je Einheit"-Badge in der Blitzkalkulations-Tabelle.
 * Reine Wrapper-Funktion (statt eines Inline-Vergleichs in der Komponente), damit die
 * Bedingung selbst unter node:test geprüft werden kann.
 */
export function zeigtEinheitModusBadge(kalkulation: PositionsKalkulation | null | undefined): boolean {
  return kalkulation?.mengenBezug === 'einheit';
}

/**
 * Blitzkalkulation: expliziter Umschalter für `mengenBezug` einer BESTEHENDEN Kalkulation
 * (bewusste Nutzer-Aktion über den Umschalter in blitzkalkulation-view.tsx — kein
 * automatisches Umstempeln). Die Zeilen bleiben unverändert; nur der Bezug ändert sich, d.h.
 * bereits eingetragene Zahlen werden ab sofort neu interpretiert (z.B. eine 7,00-€-Lohnzeile
 * gilt danach "je Einheit" statt "für die Gesamtmenge").
 *
 * Review-Auflage (GPT-5.6/Grok, 2026-08): dieser Wechsel ist KEINE harmlose Anzeigeoption —
 * er deutet JEDE Zeile neu (Lohn 7 €/m + Material 10 €/m bei 43 m: 731 € Gesamt im
 * Einheiten-Modus, 17 € im Gesamt-Modus — Faktor 43 mit einem Klick). Aufrufer DÜRFEN diese
 * Funktion deshalb nie "wortlos" anwenden: vorher über
 * {@link berechneMengenBezugWechselVorschau} EP und Gesamtpreis VORHER/NACHHER ermitteln und
 * bei `aendertPreis` eine explizite Bestätigung einholen. Die Zeilenwerte werden bewusst
 * NICHT umgerechnet (kein Skalieren der Nutzer-Zahlen) — der Wechsel ist eine vom Nutzer
 * bestätigte Neuinterpretation, keine automatische "Korrektur".
 *
 * Für eine fehlende Kalkulation (noch keine Blitz-Werte eingetragen) gibt es nichts zum
 * Umstempeln — der Umschalter in blitzkalkulation-view.tsx ist dort deaktiviert. Der frühere
 * lokale "Vormerk"-Zustand (pendingMengenBezug) wurde ersatzlos entfernt: ein Bezug, der nur
 * im UI-State lebt, läuft bei Reload/Import/Kopie/parallelem Bearbeiten auseinander (Review-
 * Fund) — der Bezug existiert jetzt ausschließlich als persistierter Modellzustand.
 */
export function wechsleMengenBezug(
  kalkulation: PositionsKalkulation,
  ziel: 'einheit' | 'gesamt',
): PositionsKalkulation {
  return { ...kalkulation, mengenBezug: ziel };
}

/** Ergebnis von {@link berechneMengenBezugWechselVorschau}. */
export interface MengenBezugWechselVorschau {
  epVorher: number;
  epNachher: number;
  gesamtVorher: number;
  gesamtNachher: number;
  /**
   * true ⇒ der Wechsel ändert EP/Gesamtpreis tatsächlich — die UI MUSS vorher/nachher
   * anzeigen und bestätigen lassen. false ⇒ der Wechsel ist wirkungslos (z.B. Menge = 1,
   * leere Kalkulation, oder Bestand ohne Feld wird nur explizit auf 'gesamt' gestellt)
   * und darf ohne Rückfrage durchgehen.
   */
  aendertPreis: boolean;
}

/**
 * Review-Auflage A (GPT-5.6/Grok, 2026-08): Vorher/Nachher-Vorschau für den
 * mengenBezug-Umschalter. Rechnet EP und Gesamtpreis (Menge × EP) einmal mit dem
 * AKTUELLEN Bezug der Kalkulation und einmal mit dem Ziel-Bezug — über exakt dieselben
 * Funktionen ({@link einheitspreisAusKalkulation} / {@link gesamtAusEinheitspreis}), die
 * auch der Speicherpfad nutzt, damit die angezeigte Vorschau nie von der tatsächlichen
 * Wirkung abweichen kann. `aendertPreis` ist das Bestätigungs-Gate: nur ein Wechsel, der
 * die Preise wirklich ändert, braucht die Rückfrage.
 */
export function berechneMengenBezugWechselVorschau(
  kalkulation: PositionsKalkulation,
  ziel: 'einheit' | 'gesamt',
  positionsMenge: number,
  defaults: KalkulationsDefaults = KALKULATION_FALLBACK_DEFAULTS,
): MengenBezugWechselVorschau {
  const epVorher = einheitspreisAusKalkulation(kalkulation, positionsMenge, defaults);
  const epNachher = einheitspreisAusKalkulation(wechsleMengenBezug(kalkulation, ziel), positionsMenge, defaults);
  return {
    epVorher,
    epNachher,
    gesamtVorher: gesamtAusEinheitspreis(epVorher, positionsMenge),
    gesamtNachher: gesamtAusEinheitspreis(epNachher, positionsMenge),
    aendertPreis: epVorher !== epNachher,
  };
}

/**
 * Review-Auflage D (Grok, 2026-08): bei Positionsmenge 1 ist der mengenBezug wirkungslos —
 * bei Menge ≠ 1 entscheidet er über den Faktor Positionsmenge. Der Nutzer muss den Bezug
 * deshalb beim ERSTEN Eintrag einer brandneuen Kalkulation bewusst sehen (unübersehbarer
 * Hinweis in blitzkalkulation-view.tsx::applyBlitz), nicht nur als Bildunterschrift.
 *
 * true ⇒ es entsteht gerade die ERSTE Kalkulation dieser Position (vorher keine vorhanden,
 * also greift der 'einheit'-Default als Vorbelegung) UND die Positionsmenge ist ≠ 1 (der
 * Bezug hat tatsächlich eine Preiswirkung). Eine bereits vorhandene Kalkulation trägt ihren
 * Bezug selbst (sichtbar im Umschalter) — dort ist keine Erst-Eingabe-Warnung nötig.
 */
export function brauchtErsteEingabeBezugsHinweis(
  bestehendeKalkulation: PositionsKalkulation | null | undefined,
  positionsMenge: number,
): boolean {
  if (bestehendeKalkulation) return false;
  const menge = toNumber(positionsMenge, Number.NaN);
  return Number.isFinite(menge) && menge > 0 && menge !== 1;
}

let zeilenIdCounter = 0;

/** Eindeutige ID für neue Kalkulationszeilen (clientseitig ausreichend). */
export function neueZeilenId(): string {
  zeilenIdCounter += 1;
  return `kz-${Date.now().toString(36)}-${zeilenIdCounter}`;
}

/**
 * Blitzkalkulation: setzt/aktualisiert genau EINE Zeile je Kategorie
 * (Lohn-Stunden, Material-EK, Sonstiges) und lässt manuell angelegte
 * Tiefenkalkulations-Zeilen unangetastet, sofern sie nicht die Blitz-Zeile sind.
 *
 * Semantik (Vorfall Malermeister, korrigiert): Die Blitzkalkulation erfasste je Zeile
 * ursprünglich den Betrag/die Stunden für die GESAMTE Position (nicht je Einheit) — z.B.
 * "2 Stunden" für die komplette Trockenbauwand. Das führte in der Praxis dazu, dass Nutzer
 * "0,1 Stunden JE EINHEIT" eintrugen, das System es aber als "0,1 Stunden GESAMT" auswertete
 * und den daraus abgeleiteten Einheitspreis durch die Positionsmenge teilte (z.B. 7,00 € statt
 * korrekt als EP übernommen ⇒ 0,16 € bei Menge 43). Für eine brandneue Kalkulation wird deshalb
 * jetzt — wie beim 'einheit'-Default der Tiefenkalkulation — explizit mengenBezug: 'einheit'
 * gesetzt (der Normalfall im Handwerk: der Betrag/die Stunden gelten JE EINHEIT). Eine bereits
 * bestehende Kalkulation (inkl. ihres mengenBezug, auch wenn das Feld dort GANZ FEHLT —
 * Bestandsdaten vor dieser Änderung) bleibt unangetastet: dieser Zweig übernimmt per Spread
 * und ergänzt/überschreibt `mengenBezug` NICHT — kein stilles Umpreisen gespeicherter
 * Kalkulationen. Der Nutzer kann den Bezug pro Position jederzeit über den Umschalter in
 * blitzkalkulation-view.tsx explizit umstellen (siehe {@link wechsleMengenBezug}).
 */
export const BLITZ_ZEILEN_IDS = {
  lohn: 'blitz-lohn',
  material: 'blitz-material',
  sonstiges: 'blitz-sonstiges',
} as const;

export function setzeBlitzZeile(
  kalkulation: PositionsKalkulation | null | undefined,
  art: keyof typeof BLITZ_ZEILEN_IDS,
  wert: { menge?: number; einzelpreis?: number },
): PositionsKalkulation {
  const basis: PositionsKalkulation = kalkulation
    ? { ...kalkulation, zeilen: [...kalkulation.zeilen] }
    : { zeilen: [], mengenBezug: 'einheit' };
  const id = BLITZ_ZEILEN_IDS[art];
  const index = basis.zeilen.findIndex((z) => z.id === id);

  const menge = wert.menge;
  const einzelpreis = wert.einzelpreis;
  const leer =
    (menge === undefined || !Number.isFinite(menge) || menge <= 0) &&
    (einzelpreis === undefined || !Number.isFinite(einzelpreis) || einzelpreis <= 0);

  if (leer) {
    if (index >= 0) basis.zeilen.splice(index, 1);
    return basis;
  }

  const vorhandene = index >= 0 ? basis.zeilen[index] : undefined;
  const neueZeile: KalkulationsZeile = {
    id,
    art,
    beschreibung: vorhandene?.beschreibung,
    menge: menge !== undefined ? menge : vorhandene?.menge,
    einzelpreis: einzelpreis !== undefined ? einzelpreis : vorhandene?.einzelpreis,
    aufschlagProzent: vorhandene?.aufschlagProzent,
    // Snapshot-Pflicht Live-Preis (Muster mengeHerkunft, client/src/lib/store.ts::updateQuantity):
    // ein manuell GEÄNDERTER Betrag löscht den Herkunftsvermerk — ein Vermerk, der behauptet
    // "dieser Betrag stammt aus Quote X", während der Betrag längst ein anderer ist, würde lügen.
    // Bleibt der Betrag unverändert (oder wird gar keiner übergeben), bleibt die Herkunft stehen.
    preisHerkunft:
      einzelpreis === undefined || einzelpreis === vorhandene?.einzelpreis
        ? vorhandene?.preisHerkunft
        : undefined,
  };
  if (index >= 0) {
    basis.zeilen[index] = neueZeile;
  } else {
    basis.zeilen.push(neueZeile);
  }
  return basis;
}

/**
 * Snapshot-Pflicht Live-Preis (Review-Auflage 2026-08): hängt den Herkunftsvermerk
 * ({@link PreisHerkunft}, shared/schema.ts) an die Blitz-MATERIAL-Zeile einer frisch über
 * den Live-Preis-Pfad gebauten Kalkulation. Additiv — alle übrigen Zeilen/Felder bleiben
 * byte-gleich. Der Aufrufer (catalog-suggest-dialog.tsx::handleApplyLive) baut die
 * Kalkulation zuerst über exakt denselben material-ek-Pfad wie Katalogpreise
 * (setzeBlitzZeile → mengenBezugFuerPreisImport) und friert DANACH die Herkunft ein —
 * Betrag und Herkunft entstehen so garantiert im selben Schritt.
 */
export function haengePreisHerkunftAnBlitzMaterial(
  kalkulation: PositionsKalkulation,
  herkunft: NonNullable<KalkulationsZeile['preisHerkunft']>,
): PositionsKalkulation {
  return {
    ...kalkulation,
    zeilen: kalkulation.zeilen.map((zeile) =>
      zeile.id === BLITZ_ZEILEN_IDS.material ? { ...zeile, preisHerkunft: herkunft } : zeile,
    ),
  };
}

/**
 * Snapshot-Pflicht Live-Preis, Editierpfad Tiefenkalkulation (Muster mengeHerkunft,
 * client/src/lib/store.ts::updateQuantity): wendet ein Teil-Update auf EINE
 * Kalkulationszeile an und LÖSCHT den Herkunftsvermerk, wenn das Update den Betrag
 * (einzelpreis) tatsächlich ÄNDERT — ein Vermerk "dieser Betrag stammt aus Quote X"
 * nach einer Handänderung würde lügen. Ein Update, das den Betrag unangetastet lässt
 * (Beschreibung, Menge, Aufschlag) oder denselben Wert erneut setzt, bewahrt die
 * Herkunft. Reine Funktion, damit sie ohne .tsx unter node:test geprüft werden kann —
 * position-kalkulation-modal.tsx::updateZeile MUSS hierüber updaten statt roh zu spreaden.
 */
export function wendeZeilenUpdateMitHerkunftsSchutzAn(
  zeile: KalkulationsZeile,
  updates: Partial<KalkulationsZeile>,
): KalkulationsZeile {
  const aendertBetrag =
    Object.prototype.hasOwnProperty.call(updates, 'einzelpreis') &&
    updates.einzelpreis !== zeile.einzelpreis;
  const naechste: KalkulationsZeile = { ...zeile, ...updates };
  if (aendertBetrag) {
    delete naechste.preisHerkunft;
  }
  return naechste;
}

/**
 * P1-1 (Fix, Codex-Fund): reine Bau-Logik für EINEN Blitzkalkulations-Zelleneintrag —
 * ausgelagert aus client/src/components/kalkulation/blitzkalkulation-view.tsx::applyBlitz,
 * damit sie ohne React/DOM unter node:test geprüft werden kann (.tsx ist nicht
 * node:test-importierbar). Baut über {@link setzeBlitzZeile} die neue Kalkulation für die
 * angegebene Blitz-Zeilenart und normalisiert eine leer gewordene Kalkulation (keine Zeilen
 * mehr, z.B. weil der Nutzer das Feld geleert hat) auf `null` — exakt das, was applyBlitz
 * vorher inline gemacht hat.
 *
 * Ob das Ergebnis tatsächlich persistiert werden darf (Netto > 0), entscheidet weiterhin
 * ALLEIN {@link darfKalkulationPersistiertWerden} innerhalb von
 * client/src/lib/store.ts::updatePositionKalkulation — dieselbe Guard-Instanz für ALLE
 * Aufrufer, keine duplizierte Kopie der Regel hier. Der Anwendungsfall, der applyBlitz bisher
 * übersah: reduziert/leert der Nutzer eine positive Zeile (z.B. Stunden) in einer Kalkulation,
 * die daneben eine FIXE negative Zeile trägt (z.B. eine Rabatt-Zeile aus
 * {@link wendeGesamtaufschlagAn}), kann das resultierende Netto ≤ 0 fallen — die Kalkulation
 * hier baut das Ergebnis trotzdem, applyBlitz muss den Store-Rückgabewert prüfen, um das zu
 * erkennen.
 */
export function baueBlitzKalkulation(
  bestehende: PositionsKalkulation | null | undefined,
  art: keyof typeof BLITZ_ZEILEN_IDS,
  wert: { menge?: number; einzelpreis?: number },
): PositionsKalkulation | null {
  const kalkulation = setzeBlitzZeile(bestehende ?? null, art, wert);
  return kalkulation.zeilen.length > 0 ? kalkulation : null;
}

/** Eingabe je Position für {@link wendeGesamtaufschlagAn}. */
export interface GesamtaufschlagPosition {
  id: number;
  /** Aktueller Einheitspreis (für Positionen OHNE Tiefenkalkulation). */
  einheitspreis: number;
  /** Positionsmenge (für die EP-Ableitung bei vorhandener Kalkulation). */
  menge: number;
  kalkulation: PositionsKalkulation | null | undefined;
}

/** Ergebnis je Position aus {@link wendeGesamtaufschlagAn}. */
export interface GesamtaufschlagErgebnis {
  id: number;
  alterEinheitspreis: number;
  neuerEinheitspreis: number;
  neueKalkulation: PositionsKalkulation | null | undefined;
}

/** Beschriftung der automatisch eingefügten Sonstiges-Zeile, z.B. "Wagnis & Gewinn (+8 %)". */
export function gesamtaufschlagZeilenBezeichnung(bezeichnung: string, prozent: number): string {
  const vorzeichen = prozent >= 0 ? '+' : '';
  const zahl = (Math.round(prozent * 100) / 100).toString().replace('.', ',');
  return `${bezeichnung} (${vorzeichen}${zahl} %)`;
}

/**
 * Wendet einen globalen Auf-/Abschlag (±Prozent, z.B. "Wagnis & Gewinn" +8 % oder
 * "Rabatt" −5 %) multiplikativ auf alle übergebenen Positionen an.
 *
 * - Positionen OHNE Tiefenkalkulation: EP wird direkt skaliert
 *   (neuerEP = roundMoney(alterEP × (1 + Prozent/100))).
 * - Positionen MIT Tiefenkalkulation: zusätzlich wird eine Sonstiges-Zeile
 *   "{Bezeichnung} (±X %)" eingefügt. Deren Betrag ist X % der BISHERIGEN
 *   Kalk-Summe (gesamtNetto) — das ist unabhängig vom mengenBezug korrekt,
 *   weil gesamtNetto bei mengenBezug 'einheit' bereits dem EP entspricht und
 *   bei 'gesamt' dem EP × Menge; in beiden Fällen skaliert die neue Zeile den
 *   abgeleiteten EP exakt um den gewünschten Faktor.
 *
 * Rein funktional (keine Store-Zugriffe) — die Anwendung selbst ist einmalig,
 * es wird kein Faktor gespeichert.
 */
export function wendeGesamtaufschlagAn(
  positionen: GesamtaufschlagPosition[],
  prozent: number,
  bezeichnung: string,
  defaults: KalkulationsDefaults = KALKULATION_FALLBACK_DEFAULTS,
): GesamtaufschlagErgebnis[] {
  return positionen.map((pos) => {
    const alterEinheitspreis = hatKalkulation(pos.kalkulation)
      ? einheitspreisAusKalkulation(pos.kalkulation, pos.menge, defaults)
      : Math.max(0, toNumber(pos.einheitspreis));

    if (hatKalkulation(pos.kalkulation) && pos.kalkulation) {
      const { gesamtNetto } = berechneKalkulation(pos.kalkulation, defaults);
      const betrag = roundMoney(gesamtNetto * (prozent / 100));
      const neueZeile: KalkulationsZeile = {
        id: neueZeilenId(),
        art: 'sonstiges',
        beschreibung: gesamtaufschlagZeilenBezeichnung(bezeichnung, prozent),
        einzelpreis: betrag,
      };
      const neueKalkulation: PositionsKalkulation = {
        ...pos.kalkulation,
        zeilen: [...pos.kalkulation.zeilen, neueZeile],
      };
      const neuerEinheitspreis = einheitspreisAusKalkulation(neueKalkulation, pos.menge, defaults);
      return { id: pos.id, alterEinheitspreis, neuerEinheitspreis, neueKalkulation };
    }

    const neuerEinheitspreis = roundMoney(alterEinheitspreis * (1 + prozent / 100));
    return { id: pos.id, alterEinheitspreis, neuerEinheitspreis, neueKalkulation: pos.kalkulation ?? null };
  });
}

/** Summe aller Einheitspreise × Menge (Netto-Gesamtsumme) — für die Vorher/Nachher-Anzeige. */
export function summeAllerPositionen(
  positionen: { einheitspreis: number; menge: number }[],
): number {
  return roundMoney(
    positionen.reduce((sum, p) => sum + Math.max(0, toNumber(p.einheitspreis)) * Math.max(0, toNumber(p.menge, 1)), 0),
  );
}

/** Eine Warnzeile aus {@link warntEinheitNachtragImTiefenModal}. */
export interface TiefenModalEinheitWarnung {
  zeilenId: string;
  art: 'lohn' | 'sonstiges';
  /** Beschreibung der Zeile (Fallback: "Lohn"/"Sonstiges"), für die Warnbox. */
  beschreibung: string;
  /** Zeilenbetrag, wie er JE EINHEIT gedeutet wird (zeilenSumme). */
  betragJeEinheit: number;
  /** betragJeEinheit × Positionsmenge — was tatsächlich im Gesamtbetrag ankommt. */
  gesamtBetrag: number;
}

/**
 * Pendant zu {@link warntMischungEinheitMitPositionszeile} für das Tiefenkalkulations-Modal
 * (client/src/components/kalkulation/position-kalkulation-modal.tsx): dort konnte eine als
 * PAUSCHALE gemeinte Lohn-/Sonstiges-Zeile still in eine 'einheit'-Kalkulation eingefügt
 * werden — JEDE Zeile wird dort je Einheit gedeutet und beim Gesamtbetrag mit der
 * Positionsmenge multipliziert (aus "Anfahrt 50 €" werden bei Menge 43 still 2.150 €).
 *
 * Reine Entscheidungsfunktion (kein UI-Zugriff), damit sie ohne .tsx unter node:test
 * prüfbar ist — die Sichtbarkeit (Warnbox) lebt im Modal. Warnt für jede in DIESER
 * Modal-Sitzung NEU HINZUGEFÜGTE Zeile (id nicht in `bestandsZeilenIds` — Bestandszeilen
 * wurden bereits mit diesem Bezug gespeichert), wenn:
 *  - die Kalkulation `mengenBezug === 'einheit'` hat,
 *  - die Positionsmenge ≠ 1 ist (bei Menge 1 ist "je Einheit" == "gesamt", nichts zu warnen),
 *  - die Zeile Lohn oder Sonstiges ist (Material-EK ist inhärent PRO EINHEIT, siehe P1-1),
 *  - die Zeile einen Betrag > 0 trägt (eine leere, gerade erst angelegte Zeile hat noch
 *    keine Zahlen, vor denen man warnen könnte).
 *
 * Nicht blockierend, keine Auto-Korrektur — sehen und verstehen: die Warnbox nennt die
 * konkreten Zahlen (Betrag je Einheit, Menge, resultierender Gesamtbeitrag).
 */
export function warntEinheitNachtragImTiefenModal(
  kalkulation: PositionsKalkulation | null | undefined,
  bestandsZeilenIds: ReadonlyArray<string>,
  positionsMenge: number,
  defaults: KalkulationsDefaults = KALKULATION_FALLBACK_DEFAULTS,
): TiefenModalEinheitWarnung[] {
  if (kalkulation?.mengenBezug !== 'einheit') return [];
  if (!Number.isFinite(positionsMenge) || positionsMenge <= 0 || positionsMenge === 1) return [];
  const bestand = new Set(bestandsZeilenIds);
  const warnungen: TiefenModalEinheitWarnung[] = [];
  for (const zeile of kalkulation.zeilen) {
    if (zeile.art !== 'lohn' && zeile.art !== 'sonstiges') continue;
    if (bestand.has(zeile.id)) continue;
    const betrag = zeilenSumme(zeile, kalkulation, defaults);
    if (betrag <= 0) continue;
    warnungen.push({
      zeilenId: zeile.id,
      art: zeile.art,
      beschreibung: zeile.beschreibung?.trim() || (zeile.art === 'lohn' ? 'Lohn' : 'Sonstiges'),
      betragJeEinheit: betrag,
      gesamtBetrag: roundMoney(betrag * positionsMenge),
    });
  }
  return warnungen;
}
